import { FirmType } from '../common/models/common.model';
import { RawMaterialEntry } from '../raw-material-inventory/raw-material.model';

export interface Supplier {
  id: string;
  name: string;
  responsible_person: string;
  gst_number: string;
  pan_card_number: string;
  udyam_number: string;
  email: string;
  phone_number: string;
  address: string;
  firm_type: FirmType;
  category: string | null; // UUID, the writable one
  category_name: string | null; // read-only, always present
}

/** The list endpoint is unpaginated: it answers with a plain array, not {count, results}. */
export interface SupplierCategory {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface SupplierCategoryRequest {
  name: string;
}

/**
 * Stands in for `uncategorised=true` in the single category dropdown. `category` and
 * `uncategorised` are mutually exclusive, so one control sends one or the other.
 * Cannot collide with a category UUID.
 */
export const SUPPLIER_UNCATEGORISED = '__uncategorised__';

export interface SupplierListFilters {
  page?: number;
  search?: string;
  category?: string;
  uncategorised?: string;
}

export interface DocumentFile {
  id: string;
  file: string; // Cloudinary file URL
  filename: string;
  content_type: string; // e.g. "application/pdf"
  size: number; // in bytes
  description: string;
  document_type: string; // e.g. "po_doc"
  document_type_display: string; // e.g. "Po Doc"
  uploaded_by_name: string;
  content_type_name: string; // e.g. "raw material purchase order"
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
}

export interface PurchaseOrder {
  id: string;
  supplier_name: string;
  warehouse_name: string;
  company_name: string | null;
  created_by_name: string;
  raw_materials: PORawMaterialItem[];
  invoices: string[];
  po_doc: DocumentFile | null;
  created_at: string;
  updated_at: string;
  po_number: string;
  entries: RawMaterialEntry[];
  date_of_order: string; // YYYY-MM-DD
  date_of_receipt: string | null; // YYYY-MM-DD or null
  total_cost: string;
  invoice_number: string | null;
  invoice_date: string | null; // YYYY-MM-DD or null
  payment_terms: string | null;
  payment_status: string | null;
  payment_date: string | null; // YYYY-MM-DD or null
  payment_amount: string | null;
  remarks: string;
  is_approved: boolean;
  approval_status: string | null;
  supplier: string; // UUID
  warehouse: string; // UUID
  company: string | null; // UUID
  created_by: string; // UUID
}

export interface PORawMaterialItem {
  id: string;
  raw_material_name: string;
  unit_name: string;
  unit_short_name: string;
  po_number: string;
  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
  quantity_ordered: number;
  rate_excl_gst: number | null;
  rate_incl_gst: number | null;
  gst_percentage: string | number | null; // rate used on this line; null on POs predating GST support
  raw_material_gst_percentage: string | number | null; // the material's current default
  amount_excl_gst: string | number | null; // per-line total, rounded by the backend
  amount_incl_gst: string | number | null;
  purchase_order: string; // UUID
  raw_material: string; // UUID
  unit: string; // UUID
}

interface PurchasedMaterial {
  quantity_ordered: number;
  raw_material: string;
  rate_excl_gst?: number | null;
  rate_incl_gst?: number | null;
  gst_percentage?: number | null;
}

export interface PurchaseOrderRequest {
  date_of_order: string;
  purchased_materials: PurchasedMaterial[];
  supplier: string;
  warehouse: string;
  company: string | null;
}
